// 운영관리: 업체 → 사이트 → 이슈 (마스터-디테일) + 서비스중 사이트 필터
import { useEffect, useState } from 'preact/hooks';
import type { Company, Site, Issue, CompanyInput } from '@shared/manage';
import {
  CONTRACT_STATUSES,
  DEPOSIT_STATUSES,
  BUILD_STATUSES,
  SUBSCRIPTION_STATUSES,
  ISSUE_STATUSES,
} from '@shared/manage';
import { api, ApiError } from '../core/ApiClient';
import { statusClass } from '../core/statusColor';
import { ConfirmDialog } from './ConfirmDialog';

function StatusSelect({
  value,
  options,
  onChange,
}: {
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
}) {
  return (
    <select
      class={`status-select ${statusClass(value)}`}
      value={value}
      onChange={(e) => onChange((e.target as HTMLSelectElement).value)}
      onClick={(e) => e.stopPropagation()}
    >
      {options.map((o) => (
        <option value={o}>{o}</option>
      ))}
    </select>
  );
}

const PAGE_SIZE = 10;

function Pager({ page, total, onPage }: { page: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  if (pages <= 1) return null;
  return (
    <div class="pager">
      <button class="btn btn--icon" disabled={page <= 0} onClick={() => onPage(page - 1)}>
        ◀
      </button>
      <span class="pager__info">
        {page + 1} / {pages}
      </span>
      <button class="btn btn--icon" disabled={page >= pages - 1} onClick={() => onPage(page + 1)}>
        ▶
      </button>
    </div>
  );
}

const wonFmt = (n: number): string => (n ? Number(n).toLocaleString('ko-KR') : '');

function CompanyFields({ company, onPatch }: { company: Company; onPatch: (p: Partial<CompanyInput>) => void }) {
  const [contact, setContact] = useState(company.contact);
  const [amount, setAmount] = useState(wonFmt(company.contractAmount));
  const [memo, setMemo] = useState(company.memo);
  useEffect(() => {
    setContact(company.contact);
    setAmount(wonFmt(company.contractAmount));
    setMemo(company.memo);
  }, [company.id]);

  return (
    <div>
    <div class="field-grid">
      <label>
        담당자 연락처
        <input
          class="input"
          value={contact}
          placeholder="담당자 / 전화번호"
          onInput={(e) => setContact((e.target as HTMLInputElement).value)}
          onBlur={() => contact !== company.contact && onPatch({ contact })}
        />
      </label>
      <label>
        계약금액(원)
        <input
          class="input"
          inputMode="numeric"
          value={amount}
          placeholder="0"
          onInput={(e) => {
            const digits = (e.target as HTMLInputElement).value.replace(/[^0-9]/g, '');
            setAmount(digits ? Number(digits).toLocaleString('ko-KR') : '');
          }}
          onBlur={() => {
            const n = Number(amount.replace(/[^0-9]/g, '')) || 0;
            if (n !== company.contractAmount) onPatch({ contractAmount: n });
          }}
        />
      </label>
    </div>
    <label class="memo-field">
      상담후 메모
      <textarea
        class="input memo-area"
        value={memo}
        placeholder="상담 내용, 특이사항 등을 기록하세요"
        onInput={(e) => setMemo((e.target as HTMLTextAreaElement).value)}
        onBlur={() => memo !== company.memo && onPatch({ memo })}
      />
    </label>
    </div>
  );
}

type Tab = 'companies' | 'service';

export function ManageView() {
  const [tab, setTab] = useState<Tab>('companies');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selCompany, setSelCompany] = useState<Company | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [selSite, setSelSite] = useState<Site | null>(null);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [serviceSites, setServiceSites] = useState<Site[]>([]);

  const [newCompany, setNewCompany] = useState('');
  const [newSite, setNewSite] = useState('');
  const [newIssue, setNewIssue] = useState('');
  const [confirm, setConfirm] = useState<{ msg: string; fn: () => void } | null>(null);
  const [err, setErr] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [companyPage, setCompanyPage] = useState(0);

  const filteredCompanies = companies.filter((c) =>
    c.name.toLowerCase().includes(companySearch.trim().toLowerCase()),
  );
  const pagedCompanies = filteredCompanies.slice(
    companyPage * PAGE_SIZE,
    companyPage * PAGE_SIZE + PAGE_SIZE,
  );

  const loadCompanies = async () => setCompanies(await api.listCompanies());
  const loadSites = async (companyId: number) => setSites(await api.listSites(companyId));
  const loadIssues = async (siteId: number) => setIssues(await api.listIssues(siteId));

  useEffect(() => {
    void loadCompanies();
  }, []);
  useEffect(() => {
    if (tab === 'service') void api.listServiceSites().then(setServiceSites);
  }, [tab]);

  const selectCompany = async (c: Company) => {
    setSelCompany(c);
    setSelSite(null);
    setIssues([]);
    await loadSites(c.id);
  };
  const selectSite = async (s: Site) => {
    setSelSite(s);
    await loadIssues(s.id);
  };

  const guard = async (fn: () => Promise<void>) => {
    setErr('');
    try {
      await fn();
    } catch (ex) {
      setErr(ex instanceof ApiError ? ex.message : '오류가 발생했습니다.');
    }
  };

  // ---- 업체 ----
  const addCompany = () =>
    guard(async () => {
      const name = newCompany.trim();
      if (!name) return;
      await api.createCompany({ name });
      setNewCompany('');
      await loadCompanies();
    });
  const patchCompany = (c: Company, patch: Partial<Company>) =>
    guard(async () => {
      await api.updateCompany(c.id, patch as never);
      await loadCompanies();
      if (selCompany?.id === c.id) setSelCompany({ ...c, ...patch });
    });
  const removeCompany = (c: Company) =>
    setConfirm({
      msg: `업체 '${c.name}' 을(를) 삭제하시겠습니까?`,
      fn: () =>
        guard(async () => {
          await api.deleteCompany(c.id);
          if (selCompany?.id === c.id) setSelCompany(null);
          await loadCompanies();
        }),
    });

  // ---- 사이트 ----
  const addSite = () =>
    guard(async () => {
      if (!selCompany) return;
      const name = newSite.trim();
      if (!name) return;
      await api.createSite({ companyId: selCompany.id, name });
      setNewSite('');
      await loadSites(selCompany.id);
      await loadCompanies();
    });
  const patchSite = (s: Site, patch: Partial<Site>) =>
    guard(async () => {
      await api.updateSite(s.id, patch as never);
      if (selCompany) await loadSites(selCompany.id);
      if (selSite?.id === s.id) setSelSite({ ...s, ...patch });
    });
  const removeSite = (s: Site) =>
    setConfirm({
      msg: `사이트 '${s.name}' 을(를) 삭제하시겠습니까?`,
      fn: () =>
        guard(async () => {
          await api.deleteSite(s.id);
          if (selSite?.id === s.id) setSelSite(null);
          if (selCompany) await loadSites(selCompany.id);
          await loadCompanies();
        }),
    });

  // ---- 이슈 ----
  const addIssue = () =>
    guard(async () => {
      if (!selSite) return;
      const title = newIssue.trim();
      if (!title) return;
      await api.createIssue({ siteId: selSite.id, title });
      setNewIssue('');
      await loadIssues(selSite.id);
      if (selCompany) await loadSites(selCompany.id);
    });
  const patchIssue = (i: Issue, patch: Partial<Issue>) =>
    guard(async () => {
      await api.updateIssue(i.id, patch as never);
      if (selSite) await loadIssues(selSite.id);
    });
  const removeIssue = (i: Issue) =>
    setConfirm({
      msg: `이슈 '${i.title}' 을(를) 삭제하시겠습니까?`,
      fn: () =>
        guard(async () => {
          await api.deleteIssue(i.id);
          if (selSite) await loadIssues(selSite.id);
          if (selCompany) await loadSites(selCompany.id);
        }),
    });

  return (
    <div class="manage">
      <div class="manage__tabs">
        <button class={`tab ${tab === 'companies' ? 'active' : ''}`} onClick={() => setTab('companies')}>
          업체 관리
        </button>
        <button class={`tab ${tab === 'service' ? 'active' : ''}`} onClick={() => setTab('service')}>
          서비스중 사이트
        </button>
      </div>

      {err && <div class="msg-error" style="margin-bottom:12px">{err}</div>}

      {tab === 'service' ? (
        <ServiceList sites={serviceSites} />
      ) : (
        <div class="manage__grid">
          {/* 업체 목록 */}
          <div class="manage__col">
            <div class="manage__col-head">업체 (최신등록순)</div>
            <div class="row" style="margin-bottom:8px">
              <input
                class="input"
                placeholder="새 업체명"
                value={newCompany}
                onInput={(e) => setNewCompany((e.target as HTMLInputElement).value)}
                onKeyDown={(e) => e.key === 'Enter' && addCompany()}
              />
              <button class="btn btn--primary btn--add" onClick={addCompany}>
                추가
              </button>
            </div>
            <input
              class="input"
              style="margin-bottom:10px"
              placeholder="🔍 업체명 검색"
              value={companySearch}
              onInput={(e) => {
                setCompanySearch((e.target as HTMLInputElement).value);
                setCompanyPage(0);
              }}
            />
            <div class="manage__list">
              {pagedCompanies.map((c) => (
                <div
                  key={c.id}
                  class={`co-card ${selCompany?.id === c.id ? 'active' : ''}`}
                  onClick={() => selectCompany(c)}
                >
                  <div class="co-card__top">
                    <span class="co-card__name">{c.name}</span>
                    <span class="co-card__count">사이트 {c.siteCount}</span>
                  </div>
                  <div class="badge-row">
                    <span class={`badge2 ${statusClass(c.contractStatus)}`}>{c.contractStatus}</span>
                    <span class={`badge2 ${statusClass(c.depositStatus)}`}>{c.depositStatus}</span>
                  </div>
                </div>
              ))}
              {filteredCompanies.length === 0 && (
                <div class="empty" style="padding:16px">
                  {companies.length === 0 ? '업체가 없습니다.' : '검색 결과가 없습니다.'}
                </div>
              )}
            </div>
            <Pager page={companyPage} total={filteredCompanies.length} onPage={setCompanyPage} />
          </div>

          {/* 상세: 사이트 + 이슈 */}
          <div class="manage__col manage__col--wide">
            {!selCompany ? (
              <div class="empty" style="padding:40px">업체를 선택하세요.</div>
            ) : (
              <div>
                <div class="detail-head">
                  <h3 style="margin:0">{selCompany.name}</h3>
                  {selCompany.contractAmount > 0 && (
                    <span class="detail-amount">계약금액 {wonFmt(selCompany.contractAmount)}원</span>
                  )}
                  <div class="spacer" />
                  <button class="btn btn--danger" onClick={() => removeCompany(selCompany)}>
                    업체 삭제
                  </button>
                </div>
                <div class="field-grid">
                  <label>
                    계약상태
                    <StatusSelect
                      value={selCompany.contractStatus}
                      options={CONTRACT_STATUSES}
                      onChange={(v) => patchCompany(selCompany, { contractStatus: v })}
                    />
                  </label>
                  <label>
                    입금상태
                    <StatusSelect
                      value={selCompany.depositStatus}
                      options={DEPOSIT_STATUSES}
                      onChange={(v) => patchCompany(selCompany, { depositStatus: v })}
                    />
                  </label>
                </div>
                <CompanyFields
                  company={selCompany}
                  onPatch={(p) => patchCompany(selCompany, p as Partial<Company>)}
                />

                {/* 사이트 목록 */}
                <div class="manage__col-head" style="margin-top:18px">제공 사이트</div>
                <div class="row" style="margin-bottom:10px">
                  <input
                    class="input"
                    placeholder="새 사이트명"
                    value={newSite}
                    onInput={(e) => setNewSite((e.target as HTMLInputElement).value)}
                    onKeyDown={(e) => e.key === 'Enter' && addSite()}
                  />
                  <button class="btn btn--primary btn--add" onClick={addSite}>
                    추가
                  </button>
                </div>
                {sites.map((s) => (
                  <div key={s.id} class={`site-card ${selSite?.id === s.id ? 'active' : ''}`}>
                    <div class="site-card__row" onClick={() => selectSite(s)}>
                      <span class="site-card__name">{s.name}</span>
                      <span class="co-card__count">이슈 {s.issueCount}</span>
                      <div class="spacer" />
                      <StatusSelect
                        value={s.buildStatus}
                        options={BUILD_STATUSES}
                        onChange={(v) => patchSite(s, { buildStatus: v })}
                      />
                      <StatusSelect
                        value={s.subscriptionStatus}
                        options={SUBSCRIPTION_STATUSES}
                        onChange={(v) => patchSite(s, { subscriptionStatus: v })}
                      />
                      <button
                        class="btn btn--icon btn--danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeSite(s);
                        }}
                      >
                        ✕
                      </button>
                    </div>

                    {/* 선택된 사이트의 이슈 히스토리 */}
                    {selSite?.id === s.id && (
                      <div class="issue-panel">
                        <div class="row" style="margin-bottom:8px">
                          <input
                            class="input"
                            placeholder="새 이슈 제목"
                            value={newIssue}
                            onInput={(e) => setNewIssue((e.target as HTMLInputElement).value)}
                            onKeyDown={(e) => e.key === 'Enter' && addIssue()}
                          />
                          <button class="btn btn--primary btn--add" onClick={addIssue}>
                            추가
                          </button>
                        </div>
                        {issues.map((i) => (
                          <div key={i.id} class="issue-row">
                            <span class="issue-row__title">{i.title}</span>
                            <span class="issue-row__date">{i.updatedAt}</span>
                            <div class="spacer" />
                            <StatusSelect
                              value={i.issueStatus}
                              options={ISSUE_STATUSES}
                              onChange={(v) => patchIssue(i, { issueStatus: v })}
                            />
                            <button class="btn btn--icon btn--danger" onClick={() => removeIssue(i)}>
                              ✕
                            </button>
                          </div>
                        ))}
                        {issues.length === 0 && (
                          <div class="empty" style="padding:12px">이슈 히스토리가 없습니다.</div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {sites.length === 0 && <div class="empty" style="padding:16px">사이트가 없습니다.</div>}
              </div>
            )}
          </div>
        </div>
      )}

      {confirm && (
        <ConfirmDialog
          title="삭제 확인"
          message={confirm.msg}
          confirmLabel="삭제"
          danger
          onConfirm={() => {
            const fn = confirm.fn;
            setConfirm(null);
            fn();
          }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

function ServiceList({ sites }: { sites: Site[] }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const q = search.trim().toLowerCase();
  const filtered = sites.filter(
    (s) =>
      s.name.toLowerCase().includes(q) || (s.companyName ?? '').toLowerCase().includes(q),
  );
  const paged = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <div class="manage__col" style="max-width:900px">
      <div class="manage__col-head">서비스중 사이트 ({filtered.length})</div>
      <input
        class="input"
        style="margin-bottom:12px"
        placeholder="🔍 업체명 · 사이트명 검색"
        value={search}
        onInput={(e) => {
          setSearch((e.target as HTMLInputElement).value);
          setPage(0);
        }}
      />
      {filtered.length === 0 ? (
        <div class="empty" style="padding:40px">
          {sites.length === 0 ? '서비스중인 사이트가 없습니다.' : '검색 결과가 없습니다.'}
        </div>
      ) : (
        <div class="manage__list" style="max-height:none">
          {paged.map((s) => (
            <div key={s.id} class="site-card">
              <div class="site-card__row">
                <span class="site-card__name">{s.name}</span>
                <span class="co-card__count">{s.companyName}</span>
                {s.url && (
                  <a href={s.url} target="_blank" rel="noreferrer" class="issue-row__date">
                    {s.url}
                  </a>
                )}
                <div class="spacer" />
                <span class={`badge2 ${statusClass(s.buildStatus)}`}>{s.buildStatus}</span>
                <span class={`badge2 ${statusClass(s.subscriptionStatus)}`}>{s.subscriptionStatus}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      <Pager page={page} total={filtered.length} onPage={setPage} />
    </div>
  );
}
